<script setup lang="ts">
import { computed, onBeforeMount, ref } from "vue";
import { useI18n } from "vue-i18n";

import { useDefaultToast } from "@/composables/useDefaultToast";
import { ModalName } from '@/constants/modals';
import { ROUTES } from '@/constants/routes';
import { formatFullName } from "@/helpers/name";
import { useAuthStore } from "@/modules/auth/store/authStore";
import { clientService } from "@/modules/clients/services/clientService";
import { buildPendingPoolWorker } from "@/modules/companyNeed/helpers/createFromWorker";
import companyNeedService from "@/modules/companyNeed/services/companyNeed";
import { useCompanyNeedStore } from "@/modules/companyNeed/store/companyNeedStore";
import { useCompanyNeedVisibilityStore } from "@/modules/companyNeed/store/companyNeedVisibilityStore";
import { useConfigurationStore } from "@/store/configurationStore";

import type { SearchCompaniesResult } from "@/modules/clients/types";
import type { CompanyNeedSourceWorker } from "@/modules/companyNeed/helpers/createFromWorker";
import type { ArchivedCompanyNeed } from "@/modules/companyNeed/types/archived";
import type { LabelValue } from '@/types/common';
import type { ICompanyDB } from '@/types/company';

import ButtonAlt from "@/dsComponents/buttons/ButtonAlt.vue";
import ComboButton from "@/dsComponents/buttons/ComboButton.vue";
import Dropdown from "@/dsComponents/Dropdown/Dropdown.vue";
import { DropdownDirections } from "@/dsComponents/Dropdown/types";
import Loader from "@/dsComponents/Loader.vue";
import Modal from "@/dsComponents/modals/Modal.vue";
import { useModalStore } from "@/dsComponents/modals/modalStore";
import QuickActionBadge from "@/dsComponents/QuickActions/QuickActionBadge.vue";
import QuickActionItem from "@/dsComponents/QuickActions/QuickActionItem.vue";
import QuickActionSearch from "@/dsComponents/QuickActions/QuickActionSearch.vue";

import router from "@/router";

interface SelectedCompany {
    company: ICompanyDB;
    archivedCompanyNeeds: ArchivedCompanyNeed[];
}

const mapSearchResultToCompanyDB = (result: SearchCompaniesResult): ICompanyDB => {
    const hasAddress = result.addressStreet || result.addressCity;
    const mainAddress = hasAddress ? { street: result.addressStreet ?? "", city: result.addressCity ?? "", zip: result.addressZip ?? "" } : undefined;

    return {
        id: result.id,
        name: result.name,
        addresses: mainAddress ? [mainAddress] : [],
        mainAddress,
        description: result.description ?? undefined,
        siret: result.siret ?? undefined,
        note: result.agencyNote ?? undefined,
        phone: result.phone ?? undefined,
        email: result.email ?? undefined,
        businessSectorId: result.businessSectorId ?? "",
        agencyId: result.ownerAgencyId ?? "",
        numbers: [],
        jobIds: [],
    } as ICompanyDB;
};

const isAbortError = (error: unknown): boolean => {
    return (error as { code?: string })?.code === ABORT_ERROR_CODE;
};

const { t } = useI18n();
const modalStore = useModalStore();
const configStore = useConfigurationStore();
const authStore = useAuthStore();
const visibilityStore = useCompanyNeedVisibilityStore();
const companyNeedStore = useCompanyNeedStore();
const { toastSuccess, toastError } = useDefaultToast();

const props = defineProps<{
    open: boolean;
    companyId?: string;
    companyName?: string;
    workers?: CompanyNeedSourceWorker[];
}>();

const emit = defineEmits<{
    close: [];
}>();

const MIN_SEARCH_LENGTH = 3;
const ABORT_ERROR_CODE = "ERR_CANCELED";

const search = ref("");
const isLoading = ref(false);
const searchAbortController = ref<AbortController>();
const companies = ref<SearchCompaniesResult[]>([]);
const selectedCompany = ref<SelectedCompany | null>(null);
const selectedCompanyNeed = ref<ArchivedCompanyNeed | null>(null);

const jobs = computed(() => configStore.jobOptions || []);
const hasSearch = computed(() => search.value.length >= MIN_SEARCH_LENGTH);
const companyNeeds = computed(() => selectedCompany.value?.archivedCompanyNeeds || []);
const isFromWorker = computed(() => !!props.workers?.length);
const workersBadgeLabel = computed(() => {
    if (!props.workers?.length) return "";
    if (props.workers.length === 1) return formatFullName(props.workers[0]);
    return t("modals.companyNeed.search.profilesSelected", { count: props.workers.length });
});

const getJobName = (jobId: string) => {
    return jobs.value.find((job: LabelValue) => job.value === jobId)?.label || "";
};

const searchCompanies = async () => {
    searchAbortController.value?.abort();
    searchAbortController.value = new AbortController();

    isLoading.value = true;
    try {
        const result = await clientService.searchCompanies(
            { q: search.value, agencyId: authStore.currentAgency?.id },
            searchAbortController.value.signal
        );
        companies.value = result.items.sort((companyA, companyB) => companyA.name.localeCompare(companyB.name));
    } catch (error: unknown) {
        if (!isAbortError(error)) {
            toastError(t("modals.companyNeed.search.errors.companies"));
        }
    } finally {
        isLoading.value = false;
    }
};

const fetchArchivedCompanyNeeds = async (company: ICompanyDB) => {
    isLoading.value = true;
    try {
        const result = await companyNeedService.fetchArchivedCompanyNeedsForCompany(company.id);
        selectedCompany.value = {
            company,
            archivedCompanyNeeds: result,
        };
        search.value = company.name;

        if (result.length === 0) {
            handleOpenCreateCompanyNeedModal();
        }
    } catch (error: unknown) {
        if (!isAbortError(error)) {
            toastError(t("modals.companyNeed.search.errors.company"));
        }
    } finally {
        isLoading.value = false;
    }
};

const handleSearch = async (value: string) => {
    search.value = value;
    selectedCompany.value = null;
    selectedCompanyNeed.value = null;
    if (hasSearch.value) {
        await searchCompanies();
    }
};

const handleClearSearch = () => {
    search.value = "";
    companies.value = [];
    selectedCompany.value = null;
    selectedCompanyNeed.value = null;
};

const handleSelectCompany = (searchResult: SearchCompaniesResult) => {
    if (isFromWorker.value) {
        openCreateCompanyNeedModal(searchResult.id);
        return;
    }
    fetchArchivedCompanyNeeds(mapSearchResultToCompanyDB(searchResult));
};

const handleSelectCompanyNeed = (companyNeed: ArchivedCompanyNeed) => {
    selectedCompanyNeed.value = selectedCompanyNeed.value === companyNeed ? null : companyNeed;
};

const openCreateCompanyNeedModal = (companyId: string) => {
    emit("close");
    modalStore.actions.open(ModalName.ManageCompanyNeedModal, {
        companyId,
        companyNeed: null,
        poolWorkerIds: props.workers?.map(({ id }) => id),
        onCreated: (companyNeedId: string) => {
            if (props.workers?.length) {
                for (const worker of props.workers) {
                    companyNeedStore.addPendingPoolWorker(companyNeedId, buildPendingPoolWorker(worker));
                }
                router.push({ name: ROUTES.APP.COMPANY_NEED.DETAIL.POOL, params: { companyNeedId } });
                return;
            }
            router.push({ name: ROUTES.APP.COMPANY_NEED.DETAIL._ROOT, params: { companyNeedId } });
        },
    });
};

const handleOpenCreateCompanyNeedModal = () => {
    if (!selectedCompany.value) return;
    openCreateCompanyNeedModal(selectedCompany.value.company.id);
};

const handleReopenCompanyNeed = async () => {
    if (!selectedCompanyNeed.value) return;

    isLoading.value = true;
    try {
        const response = await companyNeedService.reopenCompanyNeed(selectedCompanyNeed.value.id);
        visibilityStore.markReopened(selectedCompanyNeed.value.id);
        toastSuccess(t("modals.companyNeed.manage.success.reopen"));
        emit("close");
        await router.push({ name: ROUTES.APP.COMPANY_NEED.DETAIL._ROOT, params: { companyNeedId: response.id } });
    } catch (error: unknown) {
        console.error("Error reopening company need:", error);
        toastError(t("modals.companyNeed.manage.errors.reopen"));
    } finally {
        isLoading.value = false;
    }
};

onBeforeMount(async () => {
    if (!props.companyId || !props.companyName) return;

    try {
        const company = await clientService.fetchCompany(props.companyId);
        await fetchArchivedCompanyNeeds(company);
    } catch (error: unknown) {
        if (!isAbortError(error)) {
            toastError(t("modals.companyNeed.search.errors.company"));
        }
    }
});
</script>

<template>
    <Modal
        class="company-needs"
        position="top"
        width="720px"
        :open="open"
        no-padding
        no-padding-header
        no-padding-footer
        @close="emit('close')"
    >
        <template #header>
            <div class="company-needs__header">
                <QuickActionBadge v-if="workersBadgeLabel" :label="workersBadgeLabel" />
                <QuickActionSearch
                    :is-focused="open"
                    :model-value="search"
                    :placeholder="t('modals.companyNeed.search.placeholder')"
                    @update:model-value="handleSearch"
                    @clear="handleClearSearch"
                />
            </div>
        </template>
        <div v-if="isLoading" class="company-needs__loading">
            <Loader />
            <span class="company-needs__loading-label typo-xs-regular">
                {{ t("globalSearch.inProgress") }}
            </span>
        </div>
        <div v-else-if="companies.length || companyNeeds.length" class="company-needs__content">
            <template v-if="selectedCompany">
                <div class="company-needs__content-title typo-xxs-medium">
                    {{ t("modals.companyNeed.search.title") }}
                </div>
                <QuickActionItem
                    v-for="companyNeed in companyNeeds"
                    :key="companyNeed.id"
                    :selected="selectedCompanyNeed === companyNeed"
                    @click="handleSelectCompanyNeed(companyNeed)"
                >
                    <template #infos>
                        <div class="company-needs__content-company-need-item-name typo-xs-medium">
                            {{ companyNeed.name }}
                        </div>
                    </template>
                    <template #more-infos>
                        <div class="company-needs__content-company-need-item-job typo-xs-regular">
                            {{ getJobName(companyNeed.jobId) }}
                        </div>
                    </template>
                    <template v-if="companyNeed.missionsCount > 0" #first-details>
                        <Dropdown mode="hover" width="auto" :direction="DropdownDirections.TOP_CENTER">
                            <template #trigger-zone>
                                <ComboButton icon-left="order" :label="String(companyNeed.missionsCount)" />
                            </template>
                            <template #dropdown-content>
                                <span class="typo-xxs-regular">
                                    {{ t("modals.companyNeed.search.missions") }}
                                </span>
                            </template>
                        </Dropdown>
                    </template>
                    <template v-if="companyNeed.poolCount > 0" #second-details>
                        <Dropdown mode="hover" width="auto" :direction="DropdownDirections.TOP_CENTER">
                            <template #trigger-zone>
                                <ComboButton icon-left="star" :label="String(companyNeed.poolCount)" />
                            </template>
                            <template #dropdown-content>
                                <span class="typo-xxs-regular">
                                    {{ t("modals.companyNeed.search.pools") }}
                                </span>
                            </template>
                        </Dropdown>
                    </template>
                </QuickActionItem>
            </template>
            <template v-else-if="companies.length > 0">
                <QuickActionItem v-for="company in companies" :key="company.id" @click="handleSelectCompany(company)">
                    <template #infos>
                        <div class="company-needs__content-company-item-name typo-xs-medium">
                            {{ company.name }}
                        </div>
                    </template>
                    <template #more-infos>
                        <div class="company-needs__content-company-item-address typo-xs-regular">
                            {{ company.addressCity }}
                        </div>
                    </template>
                    <template v-if="company.besoinArchivedCount > 0" #first-details>
                        <Dropdown mode="hover" width="auto" :direction="DropdownDirections.TOP_CENTER">
                            <template #trigger-zone>
                                <ComboButton icon-left="folder-open" :label="String(company.besoinArchivedCount)" />
                            </template>
                            <template #dropdown-content>
                                <span class="typo-xxs-regular">
                                    {{ t("modals.companyNeed.search.archived") }}
                                </span>
                            </template>
                        </Dropdown>
                    </template>
                </QuickActionItem>
            </template>
        </div>
        <div v-else-if="!isLoading && hasSearch" class="company-needs__no-result">
            <span class="company-needs__no-result-label typo-xs-regular">
                {{ t("global.noSearchResults") }}
            </span>
        </div>
        <template v-if="selectedCompany" #footer>
            <div class="company-needs__footer">
                <div class="company-needs__footer-buttons company-needs__footer-buttons-left">
                    <ButtonAlt
                        :label="t('modals.companyNeed.search.buttons.create')"
                        left-icon="plus"
                        variant="stroke"
                        type="neutral"
                        size="sm"
                        data-bot="create-company-need"
                        @click="handleOpenCreateCompanyNeedModal"
                    />
                </div>
                <div class="company-needs__footer-buttons company-needs__footer-buttons-right">
                    <ButtonAlt
                        v-if="selectedCompanyNeed"
                        :label="t('modals.companyNeed.search.buttons.reopen')"
                        :loading="isLoading"
                        left-icon="folder-open"
                        variant="filled"
                        size="sm"
                        data-bot="reopen-company-need"
                        @click="handleReopenCompanyNeed"
                    />
                </div>
            </div>
        </template>
    </Modal>
</template>

<style scoped lang="scss">
.company-needs {
    &__header {
        display: flex;
        flex-direction: column;
        align-items: start;
        width: 100%;
        padding: var(--spacing-md, 8px) var(--spacing-lg, 12px);
    }

    &__content {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-xxs, 2px);
        padding: var(--spacing-xs, 4px) var(--spacing-xl, 16px) var(--spacing-md, 8px) 10px;

        &-title {
            padding: var(--spacing-lg, 12px) 14px var(--spacing-sm, 6px) var(--spacing-md, 8px);
            color: var(--text-sub-600, #494e61);
        }

        &-company-need-item {
            &-name,
            &-job {
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }
        }

        &-company-item {
            &-name,
            &-address {
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }
        }
    }

    &__footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: var(--spacing-lg, 12px);
        min-height: 48px;
        padding: var(--spacing-md, 8px) var(--spacing-lg, 12px);

        &-buttons {
            display: flex;
            gap: var(--spacing-md, 8px);

            &-right {
                justify-content: flex-end;
            }
        }
    }

    &__loading,
    &__no-result {
        display: flex;
        align-items: center;
        gap: var(--spacing-xs, 4px);
        height: 44px;
        padding: var(--spacing-xs, 4px) var(--spacing-3xl, 24px) var(--spacing-md, 8px);

        &-label {
            color: var(--text-sub-600, #494e61);
        }
    }
}
</style>
